/**
 * DealContactCard - Card de contato compacto
 * Visual premium com hover effect e ações rápidas
 * Inclui barra de ícones de atalho para atividades
 * 
 * Versão: 1.5.1
 * - Adicionada barra de ícones de atalho para atividades
 * - v1.4.0: Selo permanente Cliente/Contato e ações rotuladas sempre visíveis
 *   ("Cadastrar como cliente" / "Abrir contato"), sem depender de hover
 * - v1.5.1: Correção de overflow da sidebar — truncamento real de telefone/e-mail
 *   (span interno com truncate/min-w-0), min-w-0 no container raiz, overflow-hidden
 *   no bloco clicável e max-w-full no botão "Cadastrar como cliente"
 */
import React from 'react';
import { User, Phone, Mail, UserPlus } from 'lucide-react';
import { formatPhoneForDisplay } from '@/lib/phone-utils';
import { cn } from '@/lib/utils';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Button } from '@/components/ui/button';

import { QuickActivityBar, QuickActivityType } from './QuickActivityBar';
import { SocialNetworkBadge, ChannelIds } from '@/components/ui/social-network-badge';

// Props do componente - v1.2.1: Adicionado suporte a redes sociais
interface DealContactCardProps {
  contact: {
    id: string;
    name: string | null;
    phone: string | null;
    email: string | null;
    avatar_url: string | null;
    // v1.2.1: Campos de redes sociais
    channel_ids?: ChannelIds | null;
    metadata?: Record<string, unknown> | null;
  } | null;
  className?: string;
  /** v1.3.0: Indica se o contato já possui cliente vinculado */
  hasCustomer?: boolean;
  onViewContact?: (contactId: string) => void;
  /** v1.3.0: Callback para abrir cadastro de cliente a partir do contato */
  onCreateCustomer?: (contactId: string) => void;
  onQuickActivity?: (type: QuickActivityType) => void;
}

/**
 * Card compacto de contato com barra de atividades rápidas
 */
export function DealContactCard({ 
  contact,
  className,
  hasCustomer = false,
  onViewContact,
  onCreateCustomer,
  onQuickActivity
}: DealContactCardProps) {
  // Sem contato vinculado
  if (!contact) {
    return (
      <div className={cn('space-y-2', className)}>
        <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
            <User className="h-4 w-4 text-muted-foreground" />
          </div>
          <span className="text-xs text-muted-foreground">Nenhum contato vinculado</span>
        </div>
        {/* Barra de atividades mesmo sem contato */}
        {onQuickActivity && (
          <QuickActivityBar onActivityClick={onQuickActivity} />
        )}
      </div>
    );
  }

  return (
    // v1.5.1: min-w-0 no container raiz impede que filhos largos (e-mail/botão)
    // forcem a expansão da sidebar do modal de negócio
    <div className={cn('space-y-0 min-w-0', className)}>
      {/* v1.5.0: bloco do contato clicável — abre o modal 360º do contato */}
      {/* v1.5.1: overflow-hidden como barreira de contenção contra conteúdo largo */}
      <div
        className={cn(
          'flex items-center gap-2 p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors group overflow-hidden',
          onViewContact && 'cursor-pointer'
        )}
        onClick={onViewContact ? () => onViewContact(contact.id) : undefined}
      >
        <SmartAvatar 
          src={contact.avatar_url}
          name={contact.name}
          size="sm"
          defaultFallback="C"
        />
        
        <div className="min-w-0 flex-1">

          <p className="text-xs font-medium truncate">
            {contact.name || 'Sem nome'}
          </p>
          <div className="flex flex-col gap-0.5 text-[10px] text-muted-foreground min-w-0">
            {contact.phone && (
              // v1.5.1: truncate não funciona em container flex — o texto fica em
              // span interno com truncate/min-w-0 para as reticências funcionarem
              <span className="flex items-center gap-0.5 min-w-0">
                <Phone className="h-2.5 w-2.5 shrink-0" />
                <span className="truncate min-w-0">{formatPhoneForDisplay(contact.phone)}</span>
              </span>
            )}
            {contact.email && (
              <span className="flex items-center gap-0.5 min-w-0">
                <Mail className="h-2.5 w-2.5 shrink-0" />
                <span className="truncate min-w-0">{contact.email}</span>
              </span>
            )}
          </div>
          {/* v1.2.1: Exibição de redes sociais */}
          <SocialNetworkBadge 
            channelIds={contact.channel_ids}
            metadata={contact.metadata}
            variant="compact"
            showLabel={false}
          />
        </div>

        {/* v1.6.0: selo e atalho "Abrir contato" foram movidos para o header
            da seção (DealModalSidebar) — evita transbordo da sidebar */}

      </div>

      {/* v1.5.0: ação única compacta, proporcional ao tamanho da seção */}
      {!hasCustomer && onCreateCustomer && (
        <Button
          variant="outline"
          size="sm"
          // v1.5.1: max-w-full evita que o botão full-width force expansão da sidebar
          className="mt-2 h-7 w-full max-w-full gap-1.5 text-[11px] font-medium"
          onClick={() => onCreateCustomer(contact.id)}
        >
          <UserPlus className="h-3.5 w-3.5" />
          Cadastrar como cliente
        </Button>
      )}


      {/* Barra de ícones de atalho para atividades */}
      {onQuickActivity && (
        <QuickActivityBar onActivityClick={onQuickActivity} />
      )}

    </div>
  );
}
